import {Expressions} from '@packages/systems/wfdl';
import {concat, emptyArray} from '@packages/utilities/fp/utils';
import type {Expression} from '@packages/systems/wfdl-ast';

function isNonEmpty(text: string | undefined | null): text is string {
  return text != null && text.trim().length > 0;
}

/**
 * Whether the expression holds a non-empty Text at any depth, in any slot
 * (element data, attrs, bindings, …). Short-circuits on the first hit; use
 * {@link extractNonEmptyTexts} when the values themselves are needed.
 */
export const hasNonEmptyText = Expressions.some(
  (expr) => Expressions.isText(expr) && isNonEmpty(Expressions.getText(expr))
);

/**
 * Non-empty text values anywhere in the expression. Order is deterministic but
 * is NOT document order (`everything` with `concat` accumulates in reverse), so
 * the result is for comparing two traversals against each other, not for
 * reconstructing content. Order-sensitive on purpose: reordering the same texts
 * is itself a content change.
 */
export function extractNonEmptyTexts(value: Expression): string[] {
  return Expressions.everything<string[]>(concat)((expr) => {
    const text = Expressions.isText(expr) ? Expressions.getText(expr) : null;
    return Expressions.resume(isNonEmpty(text) ? [text] : emptyArray);
  })(value);
}
